import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, ALL_STATUSES, formatApiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { ArrowLeft, FileText, Mail, ExternalLink, Flag, Send } from "lucide-react";

export default function ApplicationDetail() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [job, setJob] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  // Candidate reporting
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetail, setReportDetail] = useState("");
  const [reportDone, setReportDone] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/applications/${id}`);
    setApp(data);
    setNewStatus(data.status);
    api.get(`/jobs/${data.job_id}`).then((r) => setJob(r.data)).catch(() => {});
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const updateStatus = async () => {
    setError("");
    try {
      const { data } = await api.put(`/applications/${id}/status`, { status: newStatus, note });
      setApp(data);
      setNote("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const submitReport = async () => {
    setReportError("");
    if (!reportReason) { setReportError("Select a reason."); return; }
    if (reportDetail.trim().length < 20) { setReportError("Please add at least 20 characters of context."); return; }
    setReportSubmitting(true);
    try {
      // Reports against a candidate are filed by candidate user_id with target_type='user'.
      // Moderators see candidate-reports in the Reports tab and can warn/ban/limit directly from there.
      await api.post("/reports", {
        target_type: "user",
        target_id: app.candidate_id,
        reason: reportReason,
        detail: `[CANDIDATE REPORT · application ${id}] ${reportDetail}`,
      });
      setReportDone(true);
    } catch (e) {
      setReportError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setReportSubmitting(false); }
  };

  if (!app) return <div className="p-16">Loading…</div>;

  const questionMap = {};
  (job?.questions || []).forEach((q) => { questionMap[q.id] = q.label; });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const resumeDownloadUrl = app.resume_storage_path ? `${BACKEND_URL}/api/files/${app.resume_storage_path}` : null;

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
      <Link to="/recruiter" className="inline-flex items-center gap-1 text-sm font-semibold hover:text-[var(--brand-primary)]">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>

      <div className="grid grid-cols-12 gap-10 mt-6">
        <main className="col-span-12 lg:col-span-8 space-y-8">
          <header className="border-b border-[#E2E0D8] pb-6">
            <div className="label-eyebrow">{app.company} · {app.job_title}</div>
            <h1 className="font-display font-black text-5xl mt-2 tracking-tight">{app.candidate_name || app.candidate_email}</h1>
            <div className="text-sm text-[#5C5C5C] mt-2 flex items-center gap-4 flex-wrap">
              <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {app.candidate_email}</span>
              <button
                onClick={() => {
                  const newMail = window.prompt("Enter new email address for this application:", app.candidate_email);
                  if (newMail && newMail.trim() && newMail !== app.candidate_email) {
                    api.put(`/applications/${id}/email`, { email: newMail.trim() })
                      .then((r) => {
                        setApp((prev) => ({ ...prev, candidate_email: r.data.candidate_email }));
                        alert("Candidate email updated successfully!");
                      })
                      .catch((err) => alert(err.response?.data?.detail || err.message));
                  }
                }}
                data-testid="update-app-email-btn"
                className="text-xs px-2 py-0.5 border border-[#0F0F0F] bg-[#0F0F0F] text-[#FDFCF8] hover:bg-[#FDFCF8] hover:text-[#0F0F0F] transition"
              >
                ✏️ Update Email
              </button>
              <span>Applied {new Date(app.created_at).toLocaleString()}</span>
            </div>
          </header>

          <section>
            <div className="label-eyebrow mb-3">Resume</div>
            {app.resume_url ? (
              <a href={app.resume_url} target="_blank" rel="noreferrer" data-testid="resume-link-out" className="inline-flex items-center gap-2 font-semibold hover:text-[var(--brand-primary)]">
                <FileText className="w-4 h-4" /> {app.resume_url} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            ) : resumeDownloadUrl ? (
              <a href={resumeDownloadUrl} target="_blank" rel="noreferrer" data-testid="resume-download" className="inline-flex items-center gap-2 font-semibold hover:text-[var(--brand-primary)]">
                <FileText className="w-4 h-4" /> Download uploaded resume <ExternalLink className="w-3.5 h-3.5" />
              </a>
            ) : (
              <span className="text-[#5C5C5C] italic text-sm">No resume provided.</span>
            )}
          </section>

          <section>
            <div className="label-eyebrow mb-3">Answers</div>
            <div className="space-y-4">
              {Object.entries(app.answers || {}).map(([qid, val]) => (
                <div key={qid} className="border border-[#E2E0D8] p-4">
                  <div className="text-xs uppercase tracking-widest text-[#5C5C5C]">{questionMap[qid] || qid}</div>
                  <div className="mt-1 text-base whitespace-pre-wrap">{String(val || "—")}</div>
                </div>
              ))}
              {Object.keys(app.answers || {}).length === 0 && (
                <div className="text-sm italic text-[#5C5C5C]">No custom answers.</div>
              )}
            </div>
          </section>

          <section>
            <div className="label-eyebrow mb-3">Activity</div>
            <div className="border-l-2 border-[#0F0F0F] pl-5 space-y-4">
              {[...(app.history || [])].reverse().map((h, i) => (
                <div key={i} className="relative">
                  <div className="absolute -left-[27px] top-1.5 w-3 h-3 bg-[var(--brand-primary)] rounded-full" />
                  <div className="flex items-center gap-2">
                    <StatusBadge status={h.status} />
                    <span className="text-xs text-[#5C5C5C]">{new Date(h.at).toLocaleString()} {h.by ? `· ${h.by}` : ""}</span>
                  </div>
                  {h.note && <div className="text-sm mt-1.5 text-[#3a3a3a]">{h.note}</div>}
                </div>
              ))}
            </div>
          </section>
        </main>

        <aside className="col-span-12 lg:col-span-4">
          <div className="border border-[#0F0F0F] p-6 sticky top-24">
            <div className="label-eyebrow">Current status</div>
            <div className="mt-3"><StatusBadge status={app.status} className="text-sm" /></div>

            <div className="mt-6">
              <div className="label-eyebrow mb-2">Update status</div>
              <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="status-select"
                className="w-full border border-[#0F0F0F] px-3 py-2 rounded-sm bg-transparent">
                {ALL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Add a note (optional)"
                data-testid="status-note"
                className="w-full border border-[#0F0F0F] px-3 py-2 rounded-sm bg-transparent mt-3" />
              {error && <div className="text-sm text-[var(--brand-primary)] mt-2">{error}</div>}
              <button onClick={updateStatus} data-testid="status-update-btn" className="btn-terra w-full mt-3">Update</button>
            </div>

            {app.candidate_id && (
              <div className="mt-6 pt-6 border-t border-[#E2E0D8]">
                <button
                  onClick={() => { setReportOpen(true); setReportReason(""); setReportDetail(""); setReportDone(false); setReportError(""); }}
                  data-testid="report-candidate-btn"
                  className="w-full text-xs uppercase tracking-widest font-semibold border border-[#0F0F0F] px-3 py-2 hover:bg-[#0F0F0F] hover:text-[#FDFCF8] inline-flex items-center justify-center gap-1.5"
                >
                  <Flag className="w-3.5 h-3.5" /> Report this candidate
                </button>
                <p className="text-[11px] text-[#5C5C5C] mt-2 italic">For spam, fake profiles, abusive behaviour, or fraudulent applications. Moderators review every report.</p>
              </div>
            )}
          </div>
        </aside>
      </div>

      {reportOpen && (
        <div className="fixed inset-0 z-50 bg-[#0F0F0F]/60 flex items-center justify-center p-6" onClick={() => setReportOpen(false)} data-testid="report-candidate-modal">
          <div className="bg-[#FDFCF8] border border-[#0F0F0F] p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow flex items-center gap-1.5"><Flag className="w-3 h-3 text-[var(--brand-primary)]" /> Report candidate</div>
            {!reportDone ? (
              <>
                <h3 className="font-display font-black text-2xl mt-2">{app.candidate_name || app.candidate_email}</h3>
                <p className="text-sm text-[#5C5C5C] mt-1">A moderator will review this report and may warn, limit, or ban the account if appropriate.</p>
                <label className="label-eyebrow block mt-5 mb-1.5">Reason</label>
                <select data-testid="report-reason" required value={reportReason} onChange={(e) => setReportReason(e.target.value)} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2">
                  <option value="" disabled>choose a reason…</option>
                  <option value="fake_profile">Fake or impersonation profile</option>
                  <option value="spam">Spam / mass-applied without reading</option>
                  <option value="abusive">Abusive or harassing communication</option>
                  <option value="fraud">Fraudulent claims or fabricated credentials</option>
                  <option value="off_platform">Trying to take the conversation off-platform</option>
                  <option value="other">Other (explain below)</option>
                </select>
                <label className="label-eyebrow block mt-4 mb-1.5">Details (min 20 chars)</label>
                <textarea
                  data-testid="report-detail"
                  required rows={5}
                  value={reportDetail}
                  onChange={(e) => setReportDetail(e.target.value)}
                  className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2"
                  placeholder="What happened, when, and any evidence we can verify (links, message excerpts)."
                />
                {reportError && <div data-testid="report-error" className="text-sm text-[var(--brand-primary)] mt-2">{reportError}</div>}
                <div className="flex gap-2 mt-4">
                  <button onClick={submitReport} disabled={reportSubmitting} data-testid="report-submit" className="btn-terra flex-1 disabled:opacity-50">
                    <Send className="w-4 h-4" /> {reportSubmitting ? "Sending…" : "Submit report"}
                  </button>
                  <button onClick={() => setReportOpen(false)} className="btn-ghost">Cancel</button>
                </div>
              </>
            ) : (
              <>
                <h3 className="font-display font-black text-2xl mt-2">Report received.</h3>
                <p className="text-sm text-[#5C5C5C] mt-2">Thank you — a moderator will review it within 24 hours. You don&apos;t need to do anything else. Knowingly false reports may affect your own account standing.</p>
                <button data-testid="report-close" onClick={() => setReportOpen(false)} className="btn-terra w-full mt-5">Close</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
